import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { Navigate, useTypedParams } from '@sorare/routing';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_ESSENCE,
  ANY_SPORT_ESSENCE_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { isRarityStr } from '@sorare/core/src/lib/so5';

import { CardPullExperience } from '#src/components/essence/CardPullExperience/index.tsx';

import type {
  InventoryShardsPageQuery,
  InventoryShardsPageQueryVariables,
  MarkShardsThresholdAsSeen,
  MarkShardsThresholdAsSeenVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const INVENTORY_SHARDS_TYPE_PAGE_QUERY = gql`
  query InventoryShardsPageQuery($sport: Sport!, $rarity: Rarity!) {
    currentUser {
      slug
      blockchainCardShardsChests: cardShardsChests(
        sport: $sport
        rarities: [limited, rare, super_rare, unique]
      ) {
        id
        rarity
        newThresholdUnlocked

        ...CardPullExperience_cardShardsChest
      }
      cardPull(sport: $sport) {
        id
        rarity
      }
      ...CardPullExperience_currentUser
    }
    cardPullConfigs(sport: $sport, rarity: $rarity) {
      ...CardPullExperience_cardPullConfig
    }
  }

  ${CardPullExperience.fragments.cardShardsChest}
  ${CardPullExperience.fragments.currentUser}
  ${CardPullExperience.fragments.cardPullConfig}
` as TypedDocumentNode<
  InventoryShardsPageQuery,
  InventoryShardsPageQueryVariables
>;

const MARK_SHARDS_TRESHOLD_AS_SEEN = gql`
  mutation MarkShardsThresholdAsSeen(
    $input: markCardShardsChestUnlockedThresholdAsSeenInput!
  ) {
    markCardShardsChestUnlockedThresholdAsSeen(input: $input) {
      cardShardsChest {
        id
        newThresholdUnlocked
      }
    }
  }
` as TypedDocumentNode<
  MarkShardsThresholdAsSeen,
  MarkShardsThresholdAsSeenVariables
>;

export const loader = queryLoader<PageParams>(
  INVENTORY_SHARDS_TYPE_PAGE_QUERY,
  ({ sport, type }) => ({
    sport,
    rarity: isRarityStr(type) ? type : Rarity.limited,
  })
);

const EssenceTypePageContent = () => {
  const { type } = useTypedParams<PageParams>();
  const [markAsSeen] = useMutation(MARK_SHARDS_TRESHOLD_AS_SEEN);
  const { generateSportContextPath } = useSportContext();

  const { data } = useQueryLoader<InventoryShardsPageQuery>();

  const { blockchainCardShardsChests } = data.currentUser || {};
  const shardsChest = blockchainCardShardsChests?.find(
    chest => chest.rarity === type
  );

  useEffect(() => {
    if (shardsChest?.newThresholdUnlocked) {
      markAsSeen({
        variables: {
          input: {
            cardShardsChestId: shardsChest.id,
          },
        },
      });
    }
  }, [markAsSeen, shardsChest]);

  if (!shardsChest) {
    return null;
  }

  const cardPullRarity = data.currentUser?.cardPull?.rarity;
  if (cardPullRarity && cardPullRarity !== type) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
          params: {
            type: cardPullRarity,
          },
        })}
      />
    );
  }
  return (
    <CardPullExperience
      cardPullConfigs={data.cardPullConfigs}
      cardShardsChest={shardsChest}
      currentUser={data.currentUser}
    />
  );
};

export const EssenceTypePage = () => {
  const { type } = useTypedParams<PageParams>();
  const { generateSportContextPath } = useSportContext();

  if (!isRarityStr(type)) {
    return (
      <Navigate to={generateSportContextPath(ANY_SPORT_ESSENCE)} replace />
    );
  }

  return <EssenceTypePageContent />;
};

export default EssenceTypePage;
